import {
  Vector3,
  HemisphericLight,
  PointLight,
  ShadowGenerator,
  SpotLight,
  Tools,
  DirectionalLight,
  Color3,
} from 'babylonjs' // This is core lib
import { Context3D } from '../3d-context'

// Note: Might have multiple light and diference type of light as well
// TODO: Plan function for multiple light also options for each light
// Cannot extend interface from BABYLON then we have to make our own interface

// Common light config for another developer
// If they know BABYLONJS then they can use props direct from doc.
interface ILightOptions {
  /** direction of light with data set [0,0,0] for x y z coordinate. */
  direction?: number[]
  /** position of light with data set [0,0,0] for x y z coordinate. */
  position?: number[]
  /** Intensity for dim or brighten the light. (default values is 1) */
  intensity?: number
  name?: string
}

// https://doc.babylonjs.com/divingDeeper/lights
// The Point Light - think light bulb.
// The Directional Light - think planet lit by a distant sun.
// The Spot Light - think of a focused beam of light.
// The Hemispheric Light - think of the ambient light.

/**
 * Creates default light
 */
export function setupDefaultLight(context = Context3D.get()) {
  return addHemisphericLight({})
}

export function setupDefaultLightRoom(context = Context3D.get()) {
  const light0 = addHemisphericLight({ intensity: 1 })

  const light1 = addDirectionalLight({
    name: 'light_shadow_1',
    intensity: 1,
    position: [0, 100, 0],
  })
  if (light0) {
    light0.groundColor = Color3.FromHexString('#FFFFFF')
    light0.diffuse = Color3.FromHexString('#FFFFFF')
  }
  if (light1) attachShadow(light1)
}

export function addHemisphericLight(
  options?: ILightOptions,
  context = Context3D.get()
) {
  if (!context.scene) return

  const light = new HemisphericLight(
    'HemisphericLight',
    new Vector3(-1, 1, 0), // default at center scene // direction
    context.scene
  )

  if (options) setLightOptions(options, light)

  return light
}

export function addPointLight(
  options?: ILightOptions,
  context = Context3D.get()
) {
  if (!context.scene) return

  const light = new PointLight(
    'PointLight',
    new Vector3(0, 0, 0), // default at center scene // direction
    context.scene
  )

  if (options) setLightOptions(options, light)

  return light
}

export function addSpotLight(
  options?: ILightOptions,
  context = Context3D.get()
) {
  if (!context.scene) return

  const light = new SpotLight(
    'SpotLight',
    new Vector3(0, 0, 0), // default at center scene // position
    new Vector3(0, -1, 0), // default at center scene // direction
    Tools.ToRadians(360),
    1,
    context.scene
  )

  if (options) setLightOptions(options, light)

  return light
}

export function addDirectionalLight(
  options?: ILightOptions,
  context = Context3D.get()
) {
  if (!context.scene) return

  const light = new DirectionalLight(
    'DirectionalLight',
    new Vector3(0, -1, 0), // default at center scene // direction
    context.scene
  )

  if (options) setLightOptions(options, light)

  return light
}

/**
 * Set light options after create the light, by given value and light source
 */
export function setLightOptions(
  options: ILightOptions,
  light: PointLight | HemisphericLight | SpotLight | DirectionalLight
) {
  // Prevent repeat code, then all props gonna set here
  // Mean dev can easily config light props here

  if (options?.direction) {
    const d = options.direction
    light.direction = new Vector3(d[0], d[1], d[2])
  }

  if (
    options?.position &&
    (light instanceof PointLight ||
      light instanceof SpotLight ||
      light instanceof DirectionalLight)
  ) {
    const d = options.position
    light.position = new Vector3(d[0], d[1], d[2])
  }

  if (options?.intensity) {
    light.intensity = options?.intensity
  }

  if (options?.name) {
    light.name = options?.name
    light.id = options?.name
  }
}

// On research
export function attachShadow(
  light: PointLight | SpotLight | DirectionalLight,
  context = Context3D.get()
) {
  if (!context.scene) return

  var shadowGenerator = new ShadowGenerator(256, light)
  shadowGenerator.bias = 0
  // shadowGenerator.normalBias = 1;
  // shadowGenerator.useContactHardeningShadow = true;
  // shadowGenerator.usePoissonSampling = true;
  shadowGenerator.useExponentialShadowMap = true
  shadowGenerator.useBlurExponentialShadowMap = true
  shadowGenerator.setDarkness(0.2)
  // shadowGenerator.enableSoftTransparentShadow = false;
  // shadowGenerator.transparencyShadow = true;
  shadowGenerator.blurScale = 4
  shadowGenerator.blurBoxOffset = 5

  const meshGroup = context.scene.getMeshByID('meshGroup')

  meshGroup?.getChildMeshes().forEach(function (mesh) {
    // console.log('mesh', mesh)
    shadowGenerator.addShadowCaster(mesh)
    mesh.receiveShadows = true
  })
}
